import { Link } from "react-router-dom";
import { ArrowUpRight, Phone, ShieldCheck, Clock4, FileCheck2, MapPin, Quote, Home as HomeIcon, Building2, AlertTriangle } from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import ServiceCard from "@/components/ServiceCard";
import useSeo from "@/hooks/useSeo";
import {
  SERVICES, FAQS, TESTIMONIALS, TRUST_POINTS, PHONE_DISPLAY, PHONE_TEL,
  HERO_IMAGE, ABOUT_IMAGE, SERVICE_AREAS, PROCESS_STEPS, STATS,
} from "@/lib/data";

export default function Home() {
  useSeo({
    title: "RSR Australia — Property Maintenance, Repairs & Trades",
    description: "Everything property maintenance — one trusted team. Renovations, roofing, electrical, plumbing, painting and more across Australia. Call 0410 850 550.",
  });
  return (
    <>
      {/* ============ HERO ============ */}
      <section data-testid="hero" className="relative -mt-16 md:-mt-20 pt-28 pb-16 lg:pt-36 lg:pb-24 bg-background overflow-hidden">
        <div className="relative mx-auto max-w-[1400px] w-full px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="col-span-12 lg:col-span-7">
              <div className="flex items-center gap-3 mb-8">
                <span className="rule-accent" />
                <span className="overline">Property maintenance · Australia</span>
              </div>
              <h1 className="font-display text-[44px] sm:text-6xl lg:text-[96px] leading-[0.95] tracking-tighter font-extrabold text-foreground">
                Everything property maintenance. <span className="text-accent">One trusted team.</span>
              </h1>
              <p className="mt-8 max-w-xl text-base sm:text-lg text-muted-foreground">
                Renovations, repairs, roofing, electrical, plumbing, cleaning, landscaping and the awkward odd jobs in between — handled by one crew you can actually call back.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link
                  to="/contact"
                  data-testid="hero-quote-cta"
                  className="group inline-flex items-center gap-3 bg-accent text-white px-7 py-4 font-semibold text-sm hover:bg-accent/90 transition-colors"
                >
                  Get a free quote
                  <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="hero-call-cta"
                  className="group inline-flex items-center gap-3 border border-foreground/25 hover:border-foreground text-foreground px-7 py-4 font-semibold text-sm transition-colors"
                >
                  <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
                </a>
              </div>

              <div className="mt-12 flex items-center gap-8">
                <div>
                  <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">14</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.22em] text-muted-foreground">Trade specialities</div>
                </div>
                <div className="h-12 w-px bg-border" />
                <div>
                  <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">1</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.22em] text-muted-foreground">Phone number</div>
                </div>
                <div className="h-12 w-px bg-border hidden sm:block" />
                <div className="hidden sm:block">
                  <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">7</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.22em] text-muted-foreground">Days a week</div>
                </div>
              </div>
            </div>

            <div className="col-span-12 lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-3 -left-3 right-6 bottom-10 border border-accent/40 hidden sm:block" />
                <div className="relative aspect-[4/5] overflow-hidden border border-border bg-secondary">
                  <img src={HERO_IMAGE} alt="Tradesperson at work on an Australian property" className="w-full h-full object-cover duotone" />
                </div>
                <div className="absolute -bottom-4 -right-4 bg-foreground text-background p-5 max-w-[260px] hidden sm:block">
                  <div className="text-[10px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>On-call</div>
                  <div className="mt-2 font-display text-lg font-bold leading-tight">Seven days a week, Australia-wide.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST BAR ============ */}
      <section data-testid="trust-bar" className="border-y border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="overflow-hidden no-scrollbar">
            <div className="flex items-center gap-12 py-5 marquee-track w-max">
              {[...TRUST_POINTS, ...TRUST_POINTS].map((t, i) => (
                <div key={i} className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] text-foreground/75 whitespace-nowrap">
                  <span className="w-1 h-1 rounded-full bg-accent" />
                  {t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ SERVICES GRID ============ */}
      <section data-testid="services-section" className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-8 mb-14">
            <div className="col-span-12 lg:col-span-6">
              <p className="overline">What we do</p>
              <h2 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-extrabold">
                Fourteen trades. <br /> One phone number.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-base text-muted-foreground">
                The breadth of a full property maintenance company without the call-around. Tap any tile for the details — or grab a quick quote in one click.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
            {SERVICES.map((s) => (
              <ServiceCard key={s.slug} service={s} testIdPrefix="service-card" />
            ))}
          </div>

          <div className="mt-10 flex justify-between flex-wrap gap-4">
            <Link
              to="/services"
              data-testid="all-services-link"
              className="group inline-flex items-center gap-2 text-sm font-semibold text-foreground nav-link"
            >
              View every service <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/contact" className="group inline-flex items-center gap-2 text-sm font-semibold text-accent nav-link">
              Or skip ahead — get a free quote <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ STATS BAND ============ */}
      <section data-testid="stats-band" className="border-y border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-10">
          {STATS.map((s, i) => (
            <div key={i} className="border-l border-accent pl-5">
              <div className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter leading-none">{s.k}</div>
              <div className="mt-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">{s.v}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ WHO WE HELP ============ */}
      <section data-testid="who-we-help" className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 lg:col-span-6">
              <p className="overline">Who we help</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] font-extrabold">
                Built for two <br /> kinds of caller.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8 flex items-end">
              <p className="text-base text-muted-foreground">
                Whether it's your home or your portfolio, the model's the same: one coordinator, one written quote, every trade covered. Pick the door that fits.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-border border border-border">
            {[
              {
                to: "/homeowners",
                icon: HomeIcon,
                label: "Homeowners",
                title: "Property care for your home.",
                body: "Renovations, repairs, roofing, electrical, plumbing, painting, garden and the awkward odd jobs in between. One number for every fix on the list.",
                cta: "For homeowners",
                testid: "who-we-help-homeowners",
              },
              {
                to: "/commercial",
                icon: Building2,
                label: "Property managers & business",
                title: "Multi-trade across every site.",
                body: "Reactive callouts, planned maintenance programs and full fit-outs for strata, retail, hospitality and commercial portfolios. Reporting your team can pass upstream.",
                cta: "For business",
                testid: "who-we-help-commercial",
              },
            ].map((c, i) => (
              <Link
                key={i}
                to={c.to}
                data-testid={c.testid}
                className="group relative bg-card hover:bg-secondary transition-colors p-8 lg:p-12 flex flex-col gap-6 min-h-[360px]"
              >
                <div className="flex items-start justify-between">
                  <c.icon className="w-9 h-9 text-accent" strokeWidth={1.4} />
                  <ArrowUpRight className="w-6 h-6 text-foreground/30 group-hover:text-accent transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
                <div className="mt-auto">
                  <p className="overline">{c.label}</p>
                  <h3 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold tracking-tighter leading-[1.05]">{c.title}</h3>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed max-w-md">{c.body}</p>
                </div>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent mt-2">
                  {c.cta} →
                </span>
              </Link>
            ))}
          </div>

          {/* Emergency strip */}
          <Link
            to="/emergency"
            data-testid="who-we-help-emergency"
            className="group mt-px flex items-center justify-between gap-6 bg-foreground text-background hover:bg-foreground/95 p-7 lg:p-9 transition-colors"
          >
            <div className="flex items-center gap-5">
              <div className="hidden sm:flex w-12 h-12 bg-accent items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-white" strokeWidth={1.7} />
              </div>
              <div>
                <p className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{ color: "#7AB3FF" }}>Urgent line · 7 days</p>
                <h3 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold tracking-tighter leading-tight">
                  Burst pipe? No power? <span className="text-accent">Skip the form.</span>
                </h3>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <span className="hidden md:inline font-display text-xl font-bold tracking-tight">Emergency repairs →</span>
              <ArrowUpRight className="w-6 h-6 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </div>
          </Link>
        </div>
      </section>

      {/* ============ WHY CHOOSE US ============ */}
      <section data-testid="why-us" className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 mb-12">
            <span className="rule-accent" />
            <span className="overline">Why RSR</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-border">
            {[
              { icon: ShieldCheck, title: "Quality workmanship", body: "Licensed trades, proper prep and finishes that hold up. We sign our work — and we go back if anything isn't right." },
              { icon: FileCheck2, title: "Customer satisfaction", body: "Clear quotes, honest timelines and the same coordinator on the phone from quote to clean-up. No surprises on the invoice." },
              { icon: Clock4, title: "Reliable & responsive", body: "Reactive jobs attended fast, planned work delivered when promised. Seven-day availability for urgent calls." },
            ].map((b, i) => (
              <div key={i} className="bg-card p-8 lg:p-10 flex flex-col gap-5 border-r border-b border-border">
                <b.icon className="w-6 h-6 text-accent" strokeWidth={1.5} />
                <h3 className="font-display text-2xl font-bold tracking-tight">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section data-testid="process-section" className="py-20 sm:py-28 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-10 mb-12">
            <div className="col-span-12 lg:col-span-5">
              <p className="overline">How it works</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Four steps. <br /> No runaround.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex items-end">
              <p className="text-base text-muted-foreground">
                From the first phone call to the final walk-through, here's how a job moves through RSR.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-border">
            {PROCESS_STEPS.map((step, i) => (
              <div key={i} data-testid={`process-step-${i}`} className="relative bg-background p-7 lg:p-8 border-r border-b border-border flex flex-col gap-4 min-h-[230px]">
                <div className="flex items-start justify-between">
                  <div className="font-display text-5xl font-extrabold text-accent leading-none tabular-nums">{step.n}</div>
                  {i < PROCESS_STEPS.length - 1 && (
                    <ArrowUpRight className="w-5 h-5 text-foreground/30 hidden lg:block" />
                  )}
                </div>
                <div className="mt-auto">
                  <h3 className="font-display text-xl font-bold tracking-tight">{step.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ABOUT (asymmetric) ============ */}
      <section data-testid="about-section" className="py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-8 lg:gap-14">
          <div className="col-span-12 lg:col-span-5 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-border bg-secondary">
              <img
                src={ABOUT_IMAGE}
                alt="Tradesperson with tools and gloves on a workbench"
                className="w-full h-full object-cover duotone"
              />
            </div>
            <div className="hidden lg:block absolute -bottom-8 -right-8 bg-accent text-white p-7 max-w-[260px]">
              <div className="font-display text-4xl font-extrabold leading-none">10+ yrs</div>
              <div className="mt-2 text-[11px] uppercase tracking-[0.22em] opacity-90">on the tools across Australia</div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-6 lg:col-start-7 flex flex-col justify-center">
            <p className="overline mb-5">About RSR Australia</p>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              A trade business that picks up the phone.
            </h2>
            <p className="mt-6 text-base text-muted-foreground leading-relaxed">
              RSR Australia was built on a simple idea: most property jobs need three or four trades, and chasing them down separately is a second job in itself. So we put them under one roof — licensed sparkies, plumbers, carpenters, painters, roofers and cleaners — and a coordinator who actually rings you back.
            </p>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed">
              We work for homeowners, landlords, property managers and commercial clients right across the country. Big jobs, small jobs, the ones you've been putting off for two years. We handle it, so you don't have to.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about" data-testid="about-more-link" className="group inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 text-sm font-semibold hover:bg-foreground/90 transition-colors">
                More about us <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link to="/contact" data-testid="about-quote-link" className="group inline-flex items-center gap-2 border border-border hover:border-foreground/50 px-5 py-3 text-sm font-semibold transition-colors">
                Get a quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ GALLERY removed ============ */}

      {/* ============ SERVICE AREAS ============ */}
      <section data-testid="service-areas" className="py-20 sm:py-24 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-10 items-end mb-10">
            <div className="col-span-12 lg:col-span-6">
              <p className="overline">Where we work</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Servicing every <br /> major Australian city.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-5 lg:col-start-8">
              <p className="text-base text-muted-foreground">
                Crews based in major metro and regional centres. Tell us your suburb and we'll confirm coverage on the call.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {SERVICE_AREAS.map((a) => (
              <span
                key={a}
                data-testid={`service-area-${a.toLowerCase().replace(/\s+/g, "-")}`}
                className="inline-flex items-center gap-2 px-4 py-2 border border-border bg-background text-sm font-medium hover:border-accent hover:text-accent transition-colors"
              >
                <MapPin className="w-3.5 h-3.5 text-accent" /> {a}
              </span>
            ))}
            <span className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-accent/40 text-sm font-medium text-muted-foreground">
              + more on request
            </span>
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section data-testid="testimonials" className="py-20 sm:py-28 border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 lg:col-span-5">
              <p className="overline">Said about us</p>
              <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
                Honest reviews <br /> from real clients.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <figure key={i} data-testid={`testimonial-${i}`} className="bg-card border border-border p-7 flex flex-col gap-5 hover:border-accent/40 transition-colors">
                <Quote className="w-7 h-7 text-accent" strokeWidth={1.5} />
                <blockquote className="font-display text-lg sm:text-xl leading-snug font-medium text-foreground tracking-tight">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-auto pt-4 border-t border-border">
                  <div className="text-sm font-semibold text-foreground">{t.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FAQ ============ */}
      <section data-testid="faq-section" className="py-20 sm:py-28 border-t border-border bg-card">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">FAQ</p>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tighter leading-[1.02] font-extrabold">
              Quick answers. <br className="hidden lg:block" /> No runaround.
            </h2>
            <p className="mt-6 text-sm text-muted-foreground max-w-sm">
              Can't see the question you've got? Call us or drop a quick note — we'll come back the same day.
            </p>
            <a href={`tel:${PHONE_TEL}`} data-testid="faq-call-cta" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent hover:underline">
              <Phone className="w-4 h-4" /> {PHONE_DISPLAY}
            </a>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border">
                  <AccordionTrigger
                    data-testid={`faq-trigger-${i}`}
                    className="text-left font-display text-lg sm:text-xl font-semibold tracking-tight hover:no-underline hover:text-accent py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </>
  );
}
